import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-sage-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/10a32120-472c-4553-83a2-a33e58379a7a.png" 
                alt="SayItBetter Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-semibold text-gray-800">SayItBetter</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap">Home</a>
              <a href="#how-it-works" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap">How It Works</a>
              <a href="#learning" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap">Learning</a>
              <a href="#parents" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap">For Parents</a>
              <Link to="/analyzer" className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap">
                Try Now
              </Link>
            </div>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-2xl text-gray-700"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-24 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/40 via-transparent to-green-100/40"></div>
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-shield-check-line"></i>
              <span>Safe & Supportive Communication</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Understand Messages.<br />Reply with Confidence.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
              SayItBetter helps teens navigate text conversations with friends and partners through AI-powered analysis and respectful reply suggestions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/analyzer" className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white text-base font-semibold rounded-full hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
                Start Analyzing Messages
              </Link>
              <a href="#how-it-works" className="w-full sm:w-auto px-8 py-4 bg-white text-gray-700 text-base font-semibold rounded-full hover:bg-gray-50 transition-all border-2 border-gray-200 whitespace-nowrap">
                Learn More
              </a>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-chat-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Message Analysis</h3>
              <p className="text-sm text-gray-600">Understand tone, intent, and emotional context of messages you receive</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-message-3-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Replies</h3>
              <p className="text-sm text-gray-600">Get respectful, clear reply suggestions that set healthy boundaries</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Red Flag Detection</h3>
              <p className="text-sm text-gray-600">Identify manipulation, pressure, or unhealthy communication patterns</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Simple, safe, and supportive in just three steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 h-full">
                <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-6">1</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Paste Your Message</h3>
                <p className="text-sm text-gray-700">Copy and paste the text message you received into our secure analyzer</p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 h-full">
                <div className="w-14 h-14 bg-green-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-6">2</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Get AI Analysis</h3>
                <p className="text-sm text-gray-700">Receive clear explanations about tone, intent, and any potential concerns</p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 h-full">
                <div className="w-14 h-14 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-6">3</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Choose Your Reply</h3>
                <p className="text-sm text-gray-700">Select from respectful reply suggestions or customize your own response</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Section Preview */}
      <section id="learning" className="py-20 px-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Learn Healthy Communication</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Build skills for better relationships through bite-sized lessons</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-shield-user-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">Setting Boundaries</h4>
              <p className="text-sm text-gray-600 mb-3">Learn how to communicate your limits clearly and respectfully</p>
              <Link to="/learning" className="text-sm text-blue-600 font-medium hover:text-blue-700 whitespace-nowrap">
                Start Lesson <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-emotion-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">Understanding Emotions</h4>
              <p className="text-sm text-gray-600 mb-3">Recognize emotional cues in messages and respond with empathy</p>
              <Link to="/learning" className="text-sm text-green-600 font-medium hover:text-green-700 whitespace-nowrap">
                Start Lesson <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-flag-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">Spotting Red Flags</h4>
              <p className="text-sm text-gray-600 mb-3">Identify unhealthy patterns like manipulation or pressure</p>
              <Link to="/learning" className="text-sm text-red-600 font-medium hover:text-red-700 whitespace-nowrap">
                Start Lesson <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-heart-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">Healthy Relationships</h4>
              <p className="text-sm text-gray-600 mb-3">Build strong friendships based on respect and trust</p>
              <Link to="/learning" className="text-sm text-purple-600 font-medium hover:text-purple-700 whitespace-nowrap">
                Start Lesson <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Parent Info Section */}
      <section id="parents" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-parent-line"></i>
                <span>For Parents & Guardians</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Safe, Supportive, Parent-Approved</h2>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                SayItBetter is designed with safety and healthy development at its core. We help teens build communication skills while protecting their emotional well-being.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-sm text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">No Data Storage</h4>
                    <p className="text-sm text-gray-600">Messages are analyzed in real-time and never stored on our servers</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-sm text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Age-Appropriate Content</h4>
                    <p className="text-sm text-gray-600">All guidance is designed for ages 13-18 with parent-approved language</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-sm text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">Encourages Adult Support</h4>
                    <p className="text-sm text-gray-600">We remind teens to talk to trusted adults about serious concerns</p>
                  </div>
                </div>
              </div>
              <Link to="/parent-info" className="inline-block mt-8 px-6 py-3 bg-green-600 text-white text-sm font-semibold rounded-full hover:bg-green-700 transition-colors whitespace-nowrap">
                Learn More About Safety
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/bd31d61a8d4c424991a6430d02356072.jpg" 
                alt="Parent and teen communication" 
                className="w-full h-auto rounded-2xl shadow-lg object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Simple, Affordable Pricing</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Start free, upgrade when you need more</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-sm text-gray-700">5 message analyses per day</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-sm text-gray-700">Basic tone analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-sm text-gray-700">3 reply suggestions</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-sm text-gray-700">Red flag detection</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5"></i>
                  <span className="text-sm text-gray-700">Access to learning lessons</span>
                </li>
              </ul>
              <Link to="/checkout?plan=free" className="block w-full text-center px-6 py-3 bg-gray-100 text-gray-900 text-sm font-semibold rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap">
                Start Free
              </Link>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 shadow-lg relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
                POPULAR
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Premium</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">$5</span>
                <span className="text-blue-100">/month</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-blue-200 mt-0.5"></i>
                  <span className="text-sm text-white">Unlimited message analyses</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-blue-200 mt-0.5"></i>
                  <span className="text-sm text-white">Advanced tone & intent analysis</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-blue-200 mt-0.5"></i>
                  <span className="text-sm text-white">5+ reply suggestions per message</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-blue-200 mt-0.5"></i>
                  <span className="text-sm text-white">Detailed red flag explanations</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-blue-200 mt-0.5"></i>
                  <span className="text-sm text-white">Exclusive learning content</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-blue-200 mt-0.5"></i>
                  <span className="text-sm text-white">Priority support</span>
                </li>
              </ul>
              <Link to="/checkout?plan=premium" className="block w-full text-center px-6 py-3 bg-white text-blue-600 text-sm font-semibold rounded-full hover:bg-blue-50 transition-colors whitespace-nowrap">
                Upgrade to Premium
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/10"></div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Communicate with Confidence?</h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of teens learning to navigate conversations with clarity, respect, and confidence.
          </p>
          <Link to="/analyzer" className="inline-block px-8 py-4 bg-white text-blue-600 text-base font-semibold rounded-full hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
            Start Your First Analysis
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/10a32120-472c-4553-83a2-a33e58379a7a.png" 
                  alt="SayItBetter Logo" 
                  className="h-8 w-8 object-contain"
                />
                <span className="text-lg font-semibold text-gray-800">SayItBetter</span>
              </div>
              <p className="text-sm text-gray-600">Empowering teens with healthy communication skills.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/analyzer" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Message Analyzer</Link></li>
                <li><Link to="/learning" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Learning Center</Link></li>
                <li><a href="#pricing" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/parent-info" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Parent Information</Link></li>
                <li><Link to="/disclaimer" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Safety & Disclaimer</Link></li>
                <li><a href="#help" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Help Center</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#privacy" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Privacy Policy</a></li>
                <li><a href="#terms" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="text-sm text-gray-600 mb-4 md:mb-0">© 2025 SayItBetter. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-gray-600 hover:text-blue-600 whitespace-nowrap">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
